import type { ApolloLink } from '@apollo/client/core';
import { onError } from '@apollo/client/link/error';
import { API } from '@vp/common';

// `extensions.code` of the backend's `UnauthorizedError`.
const UNAUTHORIZED_CODE = 'UNAUTHORIZED';

type SessionLapseOptions = {
  isSignedIn: () => boolean;
  hasSession: () => Promise<boolean>;
  onLapse: () => void;
};

export const createSessionLapseLink = ({ isSignedIn, hasSession, onLapse }: SessionLapseOptions): ApolloLink => {
  let probing: Promise<void> | undefined;

  // The backend's UNAUTHORIZED also covers a missing role, so only a `currentUser` probe can tell
  // a dead session from a refused permission. A probe that cannot answer keeps the session.
  const confirm = () => {
    probing ??= hasSession()
      .catch(() => true)
      .then(alive => {
        probing = undefined;
        if (!alive) {
          onLapse();
        }
      });
  };

  return onError(({ graphQLErrors, networkError, operation }) => {
    if (!isSignedIn() || operation.operationName === API.App.User.Api.SESSION_PROBE_OPERATION) {
      return;
    }

    const refused = graphQLErrors?.some(error => error.extensions?.code === UNAUTHORIZED_CODE);
    const status = (networkError as { statusCode?: number } | null | undefined)?.statusCode;

    if (refused || status === 401) {
      confirm();
    }
  });
};
